import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import type { ResonantMoment } from "@/lib/resonantMoments";

interface Props {
  moment: ResonantMoment | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

/**
 * Cinematic forensic artifact — 9:16 vertical card.
 * Quiet, premium, documentary-grade. Not influencer aesthetic.
 */
const ForensicQuoteArtifact = ({ moment, open, onOpenChange }: Props) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  if (!moment) return null;

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement("a");
      link.download = `greyrock-${moment.kind}-${Date.now()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 bg-transparent border-none shadow-none">
        <button
          onClick={() => onOpenChange(false)}
          className="absolute -top-10 right-0 text-muted-foreground/60 hover:text-foreground z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* The downloadable artifact — 9:16 */}
        <div className="flex justify-center">
          <div
            ref={cardRef}
            className="relative overflow-hidden"
            style={{
              width: 360,
              height: 640,
              background:
                "radial-gradient(120% 80% at 50% 0%, hsl(184 100% 50% / 0.06), transparent 60%), linear-gradient(180deg, hsl(0 0% 6%) 0%, hsl(0 0% 3%) 100%)",
            }}
          >
            {/* faint forensic grid */}
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.04]"
              style={{
                backgroundImage:
                  "linear-gradient(hsl(0 0% 100% / 1) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100% / 1) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
              }}
            />
            <div
              aria-hidden
              className="absolute top-0 left-0 right-0 h-px"
              style={{
                background:
                  "linear-gradient(90deg, transparent, hsl(184 100% 50% / 0.4), transparent)",
              }}
            />

            <div className="relative h-full flex flex-col justify-between p-8">
              {/* Header */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div
                    className="w-1 h-1 rounded-full"
                    style={{ background: "hsl(184 100% 50%)" }}
                  />
                  <p
                    className="text-[9px] uppercase font-mono"
                    style={{ letterSpacing: "0.32em", color: "hsl(184 100% 50% / 0.7)" }}
                  >
                    {moment.label}
                  </p>
                </div>
                <p
                  className="text-[8px] uppercase font-mono"
                  style={{ letterSpacing: "0.28em", color: "hsl(0 0% 100% / 0.35)" }}
                >
                  Forensic Observation · {moment.context}
                </p>
              </div>

              {/* The quote */}
              <div className="space-y-6">
                <div
                  className="text-[10px] font-mono"
                  style={{ letterSpacing: "0.3em", color: "hsl(0 0% 100% / 0.3)" }}
                >
                  ❝
                </div>
                <p
                  className="font-light leading-snug"
                  style={{
                    fontSize: moment.quote.length > 80 ? 22 : 26,
                    color: "hsl(0 0% 100% / 0.94)",
                    letterSpacing: "-0.005em",
                  }}
                >
                  {moment.quote}
                </p>
                <p
                  className="text-[12px] font-light leading-relaxed"
                  style={{ color: "hsl(0 0% 100% / 0.55)" }}
                >
                  {moment.observation}
                </p>
              </div>

              {/* Footer */}
              <div className="space-y-2">
                <div
                  aria-hidden
                  className="h-px w-12"
                  style={{
                    background:
                      "linear-gradient(90deg, hsl(184 100% 50% / 0.5), transparent)",
                  }}
                />
                <div className="flex items-end justify-between">
                  <div>
                    <p
                      className="text-[8px] uppercase font-mono"
                      style={{ letterSpacing: "0.32em", color: "hsl(0 0% 100% / 0.35)" }}
                    >
                      Quietly Observed
                    </p>
                    <p
                      className="text-[10px] font-semibold mt-1"
                      style={{ color: "hsl(0 0% 100% / 0.7)", letterSpacing: "0.04em" }}
                    >
                      GreyRock AI®
                    </p>
                  </div>
                  <p
                    className="text-[8px] font-mono"
                    style={{ color: "hsl(0 0% 100% / 0.25)", letterSpacing: "0.18em" }}
                  >
                    greyrockai.app
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-center">
          <Button
            onClick={handleDownload}
            disabled={exporting}
            variant="outline"
            size="sm"
            className="text-xs tracking-wider uppercase font-mono border-cyan/30 text-cyan/90 hover:bg-cyan/10"
          >
            <Download className="w-3.5 h-3.5 mr-2" />
            {exporting ? "Preserving…" : "Preserve Moment"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ForensicQuoteArtifact;
